
import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import Header from "./header";
import Footer from "./Footer";
import founder from "../assets/images/founder.png"


function useReveal() {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const reduceMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;

        if (reduceMotion) {
            setVisible(true);
            return;
        }

        const el = ref.current;

        if (!el || !("IntersectionObserver" in window)) {
            setVisible(true);
            return;
        }

        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setVisible(true);
                        io.unobserve(entry.target);
                    }
                });
            },
            {
                threshold: 0.15,
                rootMargin: "0px 0px -40px 0px"
            }
        );

        io.observe(el);

        return () => io.disconnect();
    }, []);

    return [ref, visible];
}


function Reveal({
    as: Tag = "div",
    delay = 0,
    className = "",
    children,
    ...rest
}) {
    const [ref, visible] = useReveal();

    return (
        <Tag
            ref={ref}
            className={`reveal ${visible ? "visible" : ""} ${className} `}
            style={{ "--d": `${delay} s` }}
            {...rest}
        >
            {children}
        </Tag>
    );
}


function IntroBand() {
    const bandRef = useRef(null);

    useEffect(() => {
        const reduceMotion = window.matchMedia(
            "(prefers-reduced-motion: reduce)"
        ).matches;

        const el = bandRef.current;

        if (
            !el ||
            reduceMotion ||
            !window.matchMedia("(pointer: fine)").matches
        ) {
            return;
        }

        const blobs = el.querySelectorAll(":scope > .ab-blob");

        const onMove = (e) => {
            const rect = el.getBoundingClientRect();

            const cx =
                (e.clientX - rect.left) / rect.width - 0.5;

            const cy =
                (e.clientY - rect.top) / rect.height - 0.5;

            blobs.forEach((blob, i) => {
                const strength = 18 + i * 6;

                blob.style.translate = `${cx * strength}px ${cy * strength} px`;
            });
        };

        el.addEventListener("mousemove", onMove, {
            passive: true
        });

        return () =>
            el.removeEventListener("mousemove", onMove);
    }, []);

    return (
        <section className="ab-intro-band" ref={bandRef}>
            <div className="ab-blob ab-blob1" aria-hidden="true" />
            <div className="ab-blob ab-blob2" aria-hidden="true" />

            <Container className="ab-intro-inner text-center">


                <h1
                    className="page-anim"
                    style={{ "--d": ".15s" }}
                >
                    Our reputation is built on&nbsp;
                    <span className="ab-accent">
                        Experience
                    </span>
                </h1>

                <p
                    className="ab-lede page-anim"
                    style={{ "--d": ".28s" }}
                >
                    We are a leading crypto investment platform dedicated to helping individuals and businesses grow their wealth through secure, transparent, and profitable digital asset investments.
                </p>
            </Container>
        </section>
    );
}


function StorySection() {
    return (
        <section>
            <Container className="wrap px-0">
                <Row className="align-items-start g-5 p-3">
                    <Col xs={12} md={5}>
                        <Reveal className="ab-story-figure">
                            <div className="ab-blob ab-blob3" />

                            <div className="ab-story-svg">
                                <svg
                                    viewBox="0 0 500 500"
                                    width="100%"
                                    height="100%"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    {/* Background */}
                                    <circle
                                        cx="250"
                                        cy="250"
                                        r="190"
                                        fill="var(--paper)"
                                        opacity="0.08"
                                    />

                                    {/* Outer circles */}
                                    <circle
                                        cx="250"
                                        cy="250"
                                        r="155"
                                        stroke="var(--gold)"
                                        strokeWidth="2"
                                        strokeDasharray="8 10"
                                        opacity="0.6"
                                    />

                                    <circle
                                        cx="250"
                                        cy="250"
                                        r="115"
                                        stroke="var(--gold)"
                                        strokeWidth="1.5"
                                        opacity="0.4"
                                    />

                                    {/* Crypto Coin */}
                                    <circle
                                        cx="250"
                                        cy="250"
                                        r="75"
                                        fill="var(--gold)"
                                        opacity="0.9"
                                    />

                                    <circle
                                        cx="250"
                                        cy="250"
                                        r="60"
                                        fill="none"
                                        stroke="white"
                                        strokeWidth="2"
                                        opacity="0.5"
                                    />

                                    {/* Growth Chart */}
                                    <path
                                        d="M120 340 L190 280 L240 310 L320 210 L390 150"
                                        stroke="var(--gold)"
                                        strokeWidth="8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />

                                    <path
                                        d="M360 150 H390 V180"
                                        stroke="var(--gold)"
                                        strokeWidth="8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />

                                    {/* B Icon */}
                                    <text
                                        x="250"
                                        y="275"
                                        textAnchor="middle"
                                        fill="white"
                                        fontSize="90"
                                        fontWeight="700"
                                        fontFamily="Arial"
                                    >
                                        ₿
                                    </text>

                                    {/* Small coins */}
                                    <circle
                                        cx="130"
                                        cy="180"
                                        r="28"
                                        fill="var(--gold)"
                                        opacity="0.8"
                                    />

                                    <text
                                        x="130"
                                        y="190"
                                        textAnchor="middle"
                                        fill="white"
                                        fontSize="28"
                                        fontWeight="bold"
                                    >
                                        $
                                    </text>

                                    <circle
                                        cx="380"
                                        cy="320"
                                        r="25"
                                        fill="var(--gold)"
                                        opacity="0.7"
                                    />

                                    <text
                                        x="380"
                                        y="329"
                                        textAnchor="middle"
                                        fill="white"
                                        fontSize="25"
                                        fontWeight="bold"
                                    >
                                        ↗
                                    </text>

                                    {/* Decorative dots */}
                                    <circle cx="170" cy="120" r="6" fill="var(--gold)" />
                                    <circle cx="350" cy="110" r="5" fill="var(--gold)" />
                                    <circle cx="410" cy="230" r="7" fill="var(--gold)" />
                                    <circle cx="100" cy="290" r="5" fill="var(--gold)" />
                                </svg>
                            </div>
                        </Reveal>
                    </Col>
                    <Col xs={12} md={7}>
                        <Reveal
                            delay={0.1}
                            className="ab-story-body"
                        >
                            <span className="section-eyebrow">
                                ● Our Story
                            </span>

                            <h2>
                                Built by people who closed the books
                                the hard way.
                            </h2>

                            <p>
                                We are a leading crypto investment platform dedicated to helping individuals and businesses grow their wealth through secure, transparent, and profitable digital asset investments. With a team of blockchain experts, financial analysts, and technology professionals, we provide innovative solutions that make crypto investing easy, accessible, and rewarding for everyone.
                            </p>

                            <p>
                                We specialize in crypto investment solutions that maximize profits while ensuring top-notch security. Whether you're a beginner or an experienced investor, we provide:
                                <ul>
                                    <li>Profitable Investment Plans – Short-term & long-term strategies for steady growth.</li>
                                    <li>Staking & Yield Farming – Earn passive income by locking your assets.</li>
                                    <li>Automated Trading – AI-driven trading bots to optimize your returns.</li>
                                    <li>Referral Programs – Earn commissions by inviting others to invest.</li>
                                    <li>24/7 Customer Support – Assistance whenever you need it.
                                        Our platform is designed to provide safe, seamless, and high-return investment opportunities so that you can earn while you sleep!</li>
                                </ul>
                            </p>


                        </Reveal>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}


const VALUES = [
    {
        title: "Sign Up for Free",
        text: "Create an account with just a few clicks. No hidden fees, no hassle!",
        icon: (
            <path d="M12 2l8 4v6c0 5-3.4 8.5-8 10-4.6-1.5-8-5-8-10V6l8-4z" />
        ),
    },
    {
        title: "Choose Your Investment Plan",
        text: "Select from a range of plans that match your investment goals and risk appetite.",
        icon: (
            <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
        ),
    },
    {
        title: "Deposit & Start Earning",
        text: "Make your investment using crypto or fiat currency. Watch your portfolio grow in real-time.",
        icon: (
            <path d="M4 6h16M4 12h16M4 18h10" />
        ),
    },
    {
        title: "Earn Passive Income",
        text: "Enjoy daily, weekly, or monthly returns based on your selected plan.",
        icon: (
            <path d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        ),
    },

];


function PrinciplesSection() {
    return (
        <section className="ab-principles-band my-3 py-5">
            <Container className="wrap px-0">
                <Reveal
                    className="section-head"
                    style={{ maxWidth: "640px" }}
                >
                    <span className="section-eyebrow ab-eyebrow-gold">
                        ● How it works
                    </span>

                    <h2 className="text-white">
                        Simple steps. Powerful results.
                    </h2>
                </Reveal>

                <Row className="g-3 row-cols-1 row-cols-sm-2 row-cols-lg-4">
                    {VALUES.map((v, i) => (
                        <Col key={v.title}>
                            <Reveal
                                delay={i * 0.06}
                                className="ab-value-card h-100"
                            >
                                <div className="ab-icon-badge">
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                    >
                                        {v.icon}
                                    </svg>
                                </div>

                                <h3 style={{ Color: "black" }}>{v.title}</h3>
                                <p>{v.text}</p>
                            </Reveal>
                        </Col>
                    ))}
                </Row>
            </Container>
        </section>
    );
}






function CtaBand() {
    return (
        <div className="ab-cta-band">
            <Container className="wrap px-0 d-flex flex-wrap align-items-center justify-content-between gap-4">
                <div>
                    <h2>
                        Ready to Explore New Opportunities?

                    </h2>

                    <p>
                        Join Global Inavation today and explore opportunities across global markets, investments, and financial growth.
                    </p>
                </div>

                <a
                    href="/register"
                    className="btn-global btn-dark-global btn-lg-global"
                >
                    Create Account
                </a>
            </Container>
        </div>
    );
}


export default function AboutPage() {
    useEffect(() => {
        document.body.classList.toggle("menu-open", false);
    }, []);

    return (
        <>
            <Header />

            <IntroBand />

            <StorySection />

            <PrinciplesSection />


            {/* ================= FOUNDER MESSAGE ================= */}

            <section className="gi-founder-section">

                <div className="gi-founder-content">

                    {/* Founder Image */}

                    <div className="gi-founder-image-wrapper">

                        <div className="gi-founder-image">

                            <img
                                src={founder}
                                alt="Founder"
                            />

                        </div>

                        <div className="gi-founder-badge">
                            <span>Founder</span>
                        </div>

                    </div>


                    {/* Founder Message */}

                    <div className="gi-founder-message">

                        <span className="gi-founder-label">
                            A MESSAGE FROM OUR FOUNDER
                        </span>

                        <h2>
                            Together, We Build Success
                        </h2>

                        <div className="gi-founder-quote">
                            "
                        </div>

                        <p>
                            Welcome to our growing community. Our vision has always been to
                            create an opportunity where every individual can grow, learn, and
                            build a better future through dedication and teamwork.
                        </p>

                        <p>
                            Success is not achieved overnight. It is built through consistent
                            effort, strong relationships, and the courage to take the first
                            step. Every member of our community has the potential to achieve
                            something meaningful.
                        </p>

                        <p>
                            I encourage you to believe in yourself, support your team, and
                            continue moving forward. Together, we can create a stronger
                            network and achieve greater success.
                        </p>


                        <div className="gi-founder-signature">

                            <div>

                                <h4>
                                    Chris Jakson
                                </h4>

                                <span>
                                    Founder
                                </span>

                            </div>

                        </div>

                    </div>

                </div>

            </section>
            <CtaBand />

            <Footer />
        </>
    );
}

